import { SectionBody, SectionContainer, SectionTitle } from '@agro-club/frontend-shared'
import {
  isPromocodeWithComment,
  isPromocodeWithLegalText,
  Promocode,
  PromocodeOrderType,
  PromocodeWithComment,
} from 'modules/domain/promocode/types'
import { clone } from 'ramda'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import PromocodeField, { PromocodeFieldProps } from '../OrderSkuDiscounts/OrderSkuPromocodes/PromocodeField'
import { PromocodeList, PromocodeListProps } from '../OrderSkuDiscounts/OrderSkuPromocodes/PromocodeList'

const Container = styled(SectionContainer)<{ isAllowed: boolean }>`
  transition: 0.3s all;
  position: relative;
  ${(props: { isAllowed: boolean }) =>
    props.isAllowed
      ? ''
      : css`
          opacity: 0.8;
          cursor: not-allowed;
        `}
`

const Content = styled.div<{ isAllowed: boolean }>`
  pointer-events: ${(props: { isAllowed: boolean }) => (props.isAllowed ? 'auto' : 'none')};
`

export const Total = styled.div`
  text-align: right;
  width: 100%;
`

export const TotalValue = styled.span`
  font-weight: bold;
`

export type ReturnDeclarationPromocodesProps = Omit<PromocodeListProps, 'onRemove' | 'onCommentChange'> &
  Omit<PromocodeFieldProps, 'onApply'> & {
    onPromocodesChange: (promocodes: Promocode[]) => void
    onSelectPromocode: (code: string) => void
  }

const findByCode = (code) => (item) => item.code === code

export const ReturnDeclarationPromoCodes: React.VFC<ReturnDeclarationPromocodesProps> = ({
  className,
  companyId,
  disabled,
  promocodes,
  onPromocodesChange,
  onSelectPromocode,
  ...listProps
}) => {
  const { t } = useTranslation('discount')

  const handlePromocodeApply = useCallback(
    (code: string, promocode: Promocode) => {
      const promocodesClone = clone(promocodes)
      promocodesClone.push({ ...promocode, code })
      onPromocodesChange(promocodesClone)

      if (isPromocodeWithComment(promocode) || isPromocodeWithLegalText(promocode)) {
        onSelectPromocode(code)
      }
    },
    [onPromocodesChange, onSelectPromocode, promocodes],
  )

  const handlePromocodeRemove = useCallback(
    (code) => {
      const promocodesClone = clone(promocodes)
      const index = promocodes.findIndex(findByCode(code))
      if (index !== -1) {
        promocodesClone.splice(index, 1)
      }
      onPromocodesChange(promocodesClone)
    },
    [onPromocodesChange, promocodes],
  )

  const handlePromocodeCommentChange = useCallback(
    (code, comment) => {
      const promocodesClone = clone(promocodes)
      const index = promocodesClone.findIndex(findByCode(code))
      if (index !== -1) {
        const editedPromocode = promocodesClone[index] as PromocodeWithComment
        editedPromocode.comment = comment
      }
      onPromocodesChange(promocodesClone)
    },
    [onPromocodesChange, promocodes],
  )

  return (
    <>
      {!!onPromocodesChange && (
        <Container isAllowed>
          <Content isAllowed>
            <SectionTitle>{t('promocodes')}</SectionTitle>
            <SectionBody>
              <PromocodeField
                className={className}
                companyId={companyId}
                promocodeOrderType={PromocodeOrderType.ReturnDeclaration}
                onApply={handlePromocodeApply}
                disabled={disabled}
                promocodes={promocodes}
              />
              <PromocodeList
                promocodes={promocodes}
                onRemove={handlePromocodeRemove}
                onCommentChange={handlePromocodeCommentChange}
                onSelectPromocode={onSelectPromocode}
                {...listProps}
              />
            </SectionBody>
          </Content>
        </Container>
      )}
    </>
  )
}
